import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Spin, Empty } from 'antd';
import api from '../services/api';

const GOLD = '#B8892A';
const TEAL = '#1A6B54';
const INK = '#18140C';
const MUTED = '#685840';
const BG = '#FAF8F5';
const BD = '#E8E0D0';

const STATUS_KEYS = {
  upcoming: { key: 'exhibition.upcoming', color: '#E67E22', bg: '#FEF5E7' },
  live:     { key: 'exhibition.ongoing',  color: TEAL, bg: '#E8F8F5' },
  ended:    { key: 'exhibition.ended',    color: '#95A5A6', bg: '#F2F3F4' },
};

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('zh-CN', { month: 'long', day: 'numeric' });
}

function getCountdown(startDate, t) {
  if (!startDate) return null;
  const now = new Date();
  const start = new Date(startDate);
  const diff = start - now;
  if (diff <= 0) return null;
  const days = Math.floor(diff / 86400000);
  if (days > 30) return null;
  if (days > 0) return `${days}${t('exhibition.daysUntilOpen', '天后开展')}`;
  const hours = Math.floor(diff / 3600000);
  if (hours > 0) return `${hours}${t('exhibition.hoursUntilOpen', '小时后开展')}`;
  return t('exhibition.upcoming');
}

export default function ExhibitionList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [exhibitions, setExhibitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('');
  const [flags, setFlags] = useState({});

  useEffect(() => {
    api.get('/exhibitions/flags').then(r => setFlags(r.data || {})).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (tab) params.status = tab;
    api.get('/exhibitions', { params })
      .then(r => setExhibitions(r.data?.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [tab]);

  if (!flags.exhibition_enabled && !loading) {
    return (
      <div style={{ background: BG, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🏛</div>
          <div style={{ fontSize: 18, fontWeight: 600, color: INK, marginBottom: 8 }}>{t('exhibition.comingSoon', '展会服务即将上线')}</div>
          <div style={{ fontSize: 14, color: MUTED }}>{t('exhibition.stayTuned', '敬请期待')}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: BG, minHeight: '100vh', paddingBottom: 40 }}>
      <div style={{ maxWidth: 800, margin: '0 auto' }}>
        {/* Header Banner */}
        <div style={{
          background: 'linear-gradient(135deg, #1A2E28 0%, #2A4A3E 50%, #1A2E28 100%)',
          padding: '32px 24px', textAlign: 'center',
        }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#fff', letterSpacing: 4, marginBottom: 6 }}>
            {t('exhibition.title')}
          </div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,.5)', letterSpacing: 2 }}>
            {t('exhibition.slogan', '透明报价 · 在线预约 · 智能逛展')}
          </div>
        </div>

        {/* Status Tabs */}
        <div style={{
          display: 'flex', background: '#fff', borderBottom: `1px solid ${BD}`,
          padding: '0 16px',
        }}>
          {[
            { key: '', label: t('common.all') },
            { key: 'live', label: t('exhibition.ongoing') },
            { key: 'upcoming', label: t('exhibition.upcoming') },
            { key: 'ended', label: t('exhibition.pastExhibitions', '往期') },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              padding: '12px 16px', border: 'none', cursor: 'pointer',
              fontSize: 14, fontWeight: tab === t.key ? 600 : 400,
              color: tab === t.key ? TEAL : MUTED,
              background: 'transparent',
              borderBottom: tab === t.key ? `2px solid ${TEAL}` : '2px solid transparent',
            }}>{t.label}</button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>
        ) : exhibitions.length === 0 ? (
          <div style={{ padding: 60 }}><Empty description={t('exhibition.noExhibitions', '暂无展会')} /></div>
        ) : (
          <div style={{ padding: '16px' }}>
            {exhibitions.map(exh => {
              const ss = STATUS_KEYS[exh.status] || STATUS_KEYS.ended;
              const countdown = exh.status === 'upcoming' ? getCountdown(exh.start_date, t) : null;
              return (
                <button key={exh.id} onClick={() => navigate(`/exhibitions/${exh.id}`)} style={{
                  width: '100%', textAlign: 'left', display: 'block',
                  background: '#fff', borderRadius: 12, border: `1px solid ${BD}`,
                  marginBottom: 14, overflow: 'hidden', cursor: 'pointer', padding: 0,
                }}>
                  {/* Banner */}
                  {exh.banner_url && (
                    <div style={{
                      height: 140, background: `url(${exh.banner_url}) center/cover`,
                    }} />
                  )}
                  {!exh.banner_url && (
                    <div style={{
                      height: 100,
                      background: 'linear-gradient(135deg, #2A1A0A 0%, #3A2A1A 100%)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <span style={{ fontSize: 32 }}>🏛</span>
                    </div>
                  )}

                  <div style={{ padding: '14px 16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                      <div style={{ fontSize: 16, fontWeight: 600, color: INK, flex: 1 }}>
                        {exh.name}
                      </div>
                      <span style={{
                        fontSize: 11, padding: '2px 8px', borderRadius: 4, flexShrink: 0,
                        background: ss.bg, color: ss.color, fontWeight: 500,
                      }}>{t(ss.key)}</span>
                    </div>
                    <div style={{ fontSize: 12, color: MUTED, marginBottom: 4 }}>
                      📍 {exh.venue || exh.city || '—'}
                    </div>
                    <div style={{ fontSize: 12, color: MUTED, marginBottom: 8 }}>
                      📅 {formatDate(exh.start_date)} — {formatDate(exh.end_date)}
                    </div>
                    <div style={{ display: 'flex', gap: 16, fontSize: 12, color: MUTED, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span>🏪 {exh.booth_count || 0} {t('exhibition.booths', '展位')}</span>
                      <span>💎 {exh.item_count || 0} {t('exhibition.exhibits', '件展品')}</span>
                      {exh.organizer && <span>{t('exhibition.organizer', '主办')}: {exh.organizer}</span>}
                      {countdown && (
                        <span style={{
                          fontSize: 11, padding: '2px 10px', borderRadius: 10,
                          background: '#FEF5E7', color: '#E67E22', fontWeight: 600,
                        }}>{countdown}</span>
                      )}
                      {exh.status === 'live' && (
                        <span style={{
                          fontSize: 11, padding: '2px 10px', borderRadius: 10,
                          background: '#E8F8F5', color: TEAL, fontWeight: 600,
                        }}>{t('exhibition.onlineSamePrice', '展品线上同价')}</span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
